import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Pencil, Save, X as CloseIcon, Layers } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface DimensionSpec {
  id: string;
  tenantId: string;
  name: string;
  label: string;
  kind: 'ENUM' | 'RANGE' | 'NAMED_LIST';
  sourceType: 'DERIVED' | 'CUSTOM_FIELD' | 'MANUAL';
  sourceRef: string | null;
  values: Record<string, unknown>;
  isActive: boolean;
  displayOrder: number;
  createdAt: string | null;
  updatedAt: string | null;
}

const KIND_OPTIONS: DimensionSpec['kind'][] = ['ENUM', 'RANGE', 'NAMED_LIST'];
const SOURCE_OPTIONS: DimensionSpec['sourceType'][] = ['DERIVED', 'CUSTOM_FIELD', 'MANUAL'];

function emptyValuesFor(kind: DimensionSpec['kind']): Record<string, unknown> {
  switch (kind) {
    case 'ENUM':
      return { values: [{ key: '', label: '' }] };
    case 'RANGE':
      return { bands: [{ key: '', label: '', min: 0, max: null }] };
    case 'NAMED_LIST':
      return { lists: [{ key: '', label: '', entity_ids: [] }] };
  }
}

/**
 * Story 59.7 — /admin/segmentation page.
 *
 * Per ADR-007, lets a tenant admin declare the axes their team
 * segments by (region, industry, sector, deal-tier, named-account, …).
 * Each dimension has a kind, a value source, and a value catalogue
 * that's edited inline. The funnel filter, dashboard group-by, lead
 * routing rules, and commission tiers all read from this single model.
 */
export function SegmentationPage() {
  const { t } = useTranslation();
  const [dims, setDims] = useState<DimensionSpec[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<DimensionSpec | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    apiClient
      .get<DimensionSpec[]>('/segmentation/dimensions', {
        params: { includeInactive: true },
      })
      .then(({ data }) => {
        setDims(data);
        setError(null);
      })
      .catch(() => setError(t('segmentation.error_load')))
      .finally(() => setLoading(false));
  }

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Layers className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold">{t('segmentation.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('segmentation.subtitle')}</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t('segmentation.new_dimension')}
        </Button>
      </header>

      {error && <Card><p className="text-sm text-destructive">{error}</p></Card>}

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : dims.length === 0 && !creating ? (
        <Card>
          <p className="text-sm text-muted-foreground">{t('segmentation.empty')}</p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {dims.map((d) => (
            <li key={d.id}>
              <DimensionCard
                dimension={d}
                onEdit={() => setEditing(d)}
                onDelete={async () => {
                  if (!confirm(t('segmentation.confirm_delete'))) return;
                  await apiClient.delete(`/segmentation/dimensions/${d.id}`);
                  load();
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <DimensionEditor
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function DimensionCard({
  dimension,
  onEdit,
  onDelete,
}: {
  dimension: DimensionSpec;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const summary = useMemo(() => summarise(dimension), [dimension]);
  return (
    <Card className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">{dimension.label}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {dimension.kind}
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {dimension.sourceType}
            </span>
            {!dimension.isActive && (
              <span className="rounded-full bg-rose-900/40 px-2 py-0.5 text-xs text-rose-200">
                {t('segmentation.inactive')}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            <code>{dimension.name}</code>
            {dimension.sourceRef && <span className="ml-2 font-mono">→ {dimension.sourceRef}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="mr-1 h-3.5 w-3.5" />
            {t('common.edit')}
          </Button>
          <Button size="sm" variant="destructive" onClick={onDelete}>
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            {t('common.delete')}
          </Button>
        </div>
      </div>
      {summary.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {summary.map((s) => (
            <span
              key={s}
              className="rounded-full border bg-card px-2 py-0.5 text-xs text-muted-foreground"
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function DimensionEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: DimensionSpec | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [kind, setKind] = useState<DimensionSpec['kind']>(initial?.kind ?? 'ENUM');
  const [sourceType, setSourceType] = useState<DimensionSpec['sourceType']>(
    initial?.sourceType ?? 'MANUAL',
  );
  const [sourceRef, setSourceRef] = useState(initial?.sourceRef ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [valuesJson, setValuesJson] = useState(
    JSON.stringify(initial?.values ?? emptyValuesFor(initial?.kind ?? 'ENUM'), null, 2),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When kind changes, reset values_json to a sensible default for
  // that kind so the user has a usable starting shape.
  function handleKindChange(next: DimensionSpec['kind']) {
    setKind(next);
    setValuesJson(JSON.stringify(emptyValuesFor(next), null, 2));
    if (next === 'RANGE' && sourceType === 'MANUAL') setSourceType('DERIVED');
    if (next === 'NAMED_LIST') setSourceType('MANUAL');
  }

  async function save() {
    setSaving(true);
    setError(null);
    let values: Record<string, unknown>;
    try {
      values = JSON.parse(valuesJson);
    } catch {
      setError(t('segmentation.invalid_json'));
      setSaving(false);
      return;
    }
    const payload = {
      name,
      label,
      kind,
      sourceType,
      sourceRef: sourceRef.trim() || null,
      values,
      isActive,
      displayOrder: initial?.displayOrder ?? 0,
    };
    try {
      if (initial) {
        await apiClient.put(`/segmentation/dimensions/${initial.id}`, payload);
      } else {
        await apiClient.post('/segmentation/dimensions', payload);
      }
      onSaved();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        t('segmentation.error_save');
      setError(detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl space-y-3 rounded-lg border bg-card p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {initial ? t('segmentation.edit_title') : t('segmentation.new_dimension')}
          </h3>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <CloseIcon className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('segmentation.field_name')}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value.replace(/[^a-z0-9_]/g, '_'))}
              placeholder="region"
              disabled={!!initial}
            />
            <p className="text-[10px] text-muted-foreground">{t('segmentation.name_hint')}</p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('segmentation.field_label')}</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Region"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium">{t('segmentation.field_kind')}</label>
            <select
              value={kind}
              onChange={(e) => handleKindChange(e.target.value as DimensionSpec['kind'])}
              className="w-full rounded-md border bg-transparent px-2 py-1.5 text-sm"
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('segmentation.field_source')}</label>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as DimensionSpec['sourceType'])}
              className="w-full rounded-md border bg-transparent px-2 py-1.5 text-sm"
            >
              {SOURCE_OPTIONS.map((s) => (
                <option
                  key={s}
                  value={s}
                  disabled={
                    (kind === 'RANGE' && s === 'MANUAL') ||
                    (kind === 'NAMED_LIST' && s !== 'MANUAL')
                  }
                >
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {sourceType !== 'MANUAL' && (
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('segmentation.field_source_ref')}</label>
            <Input
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
              placeholder={
                sourceType === 'DERIVED'
                  ? 'accounts.industry'
                  : 'custom_field_definition_id (UUID)'
              }
            />
            <p className="text-[10px] text-muted-foreground">
              {sourceType === 'DERIVED'
                ? t('segmentation.derived_hint')
                : t('segmentation.custom_field_hint')}
            </p>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs font-medium">{t('segmentation.field_values_json')}</label>
          <textarea
            value={valuesJson}
            onChange={(e) => setValuesJson(e.target.value)}
            rows={10}
            className="w-full rounded-md border bg-transparent px-2 py-1.5 font-mono text-xs"
          />
          <p className="text-[10px] text-muted-foreground">
            {kind === 'ENUM' && t('segmentation.values_enum_hint')}
            {kind === 'RANGE' && t('segmentation.values_range_hint')}
            {kind === 'NAMED_LIST' && t('segmentation.values_named_hint')}
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4"
          />
          {t('segmentation.is_active')}
        </label>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !name || !label}>
            <Save className="mr-1 h-3.5 w-3.5" />
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function summarise(d: DimensionSpec): string[] {
  if (d.kind === 'ENUM') {
    const arr = (d.values as { values?: Array<{ key?: string; label?: string }> }).values ?? [];
    return arr.slice(0, 6).map((v) => v.label ?? v.key ?? '');
  }
  if (d.kind === 'RANGE') {
    const arr = (d.values as { bands?: Array<{ key?: string; label?: string }> }).bands ?? [];
    return arr.slice(0, 6).map((v) => v.label ?? v.key ?? '');
  }
  if (d.kind === 'NAMED_LIST') {
    const arr = (d.values as { lists?: Array<{ key?: string; label?: string }> }).lists ?? [];
    return arr.slice(0, 6).map((v) => v.label ?? v.key ?? '');
  }
  return [];
}
